 import React, { useState, useEffect } from 'react';
import { 
  FiTool, 
  FiBarChart2, 
  FiSettings, 
  FiShield,
  FiChevronRight,
  FiDroplet,
  FiMapPin,
  FiSun
} from 'react-icons/fi';
import { getTimeAgo } from '../../../../../utils/statusUtils';
import DeviceManagementTools from '../DeviceManagementTools';
import DispenseChart from '../../../../common/DispenseChart';
import HealthTab from '../HealthTab';
import WeatherTab from '../WeatherTab';
import SlotManager from '../SlotManager';
import HardwareHealthTracker from '../HardwareHealthTracker';
import api from '../../../../../services/api';
import './BlockDetailsView.css';

const BlockDetailsView = ({ 
  block, 
  isExpanded, 
  onUpdateLocation,
  onUpdateAddress,
  onDeviceAction,
  onConfirmDangerousAction,
  onRefillSlot,
  userRole,
  isAdmin,
  isFranchisee,
  isOperator 
}) => {
  const [activeTab, setActiveTab] = useState('tools');
  const [expandedSections, setExpandedSections] = useState({});
  const [newLocation, setNewLocation] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [dispenseHistory, setDispenseHistory] = useState([]);
  const [loadingDispenseHistory, setLoadingDispenseHistory] = useState(false);
  const [dispenseHistoryError, setDispenseHistoryError] = useState(null);

  const { 
    serial, 
    address, 
    location,
    battery_charging,
    temperature_f,
    humidity,
    weather_data
  } = block || {};

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fetch dispense history from block_data collection
  const fetchDispenseHistory = async () => {
    if (!serial) return;
    
    setLoadingDispenseHistory(true);
    setDispenseHistoryError(null);
    
    try {
      console.log('BlockDetailsView: Fetching dispense history for:', serial);
      const response = await api.getDispenseHistory(serial);
      
      console.log('BlockDetailsView: Dispense history response:', response);
      console.log('BlockDetailsView: Response type:', typeof response);
      console.log('BlockDetailsView: Response keys:', response ? Object.keys(response) : 'null');
      console.log('BlockDetailsView: Response.data:', response?.data);
      console.log('BlockDetailsView: Response.data type:', typeof response?.data);
      console.log('BlockDetailsView: Response.data is array:', Array.isArray(response?.data));
      
      // Check if response is directly an array (backend might be returning array directly)
      if (Array.isArray(response)) {
        setDispenseHistory(response);
        console.log('BlockDetailsView: Set dispense history (direct array):', response.length, 'records');
      } else if (response && response.data && Array.isArray(response.data)) {
        setDispenseHistory(response.data);
        console.log('BlockDetailsView: Set dispense history (response.data):', response.data.length, 'records');
      } else if (response && Array.isArray(response.result)) {
        // Check if it's in response.result
        setDispenseHistory(response.result);
        console.log('BlockDetailsView: Set dispense history (response.result):', response.result.length, 'records');
      } else {
        console.warn('BlockDetailsView: Invalid dispense history response format:', response);
        console.warn('BlockDetailsView: Expected array or object with data/result property');
        setDispenseHistory([]);
      }
    } catch (error) {
      console.error('BlockDetailsView: Error fetching dispense history:', error);
      setDispenseHistoryError(error.message);
      setDispenseHistory([]);
    } finally {
      setLoadingDispenseHistory(false);
    }
  };

  // Fetch dispense history when component mounts or serial changes
  useEffect(() => {
    fetchDispenseHistory();
  }, [serial]);



  const handleLocationUpdate = (e) => {
    e.preventDefault();
    if (newLocation.trim()) {
      onUpdateLocation?.(serial, newLocation);
      setNewLocation('');
    }
  };

  const handleAddressUpdate = (e) => {
    e.preventDefault();
    if (newAddress.trim()) {
      onUpdateAddress?.(serial, newAddress);
      setNewAddress('');
    }
  };



  const getLastSeenDisplay = () => {
    const timestamp = block.last_seen || block.lastHeartbeat;
    if (!timestamp) return 'Never';
    
    // Use the global utility for consistent time formatting
    return getTimeAgo(new Date(timestamp));
  };

  const tabs = [
    { id: 'tools', label: 'Tools', icon: FiTool },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart2 },
    { id: 'sunscreen', label: 'Sunscreen', icon: FiDroplet },
    { id: 'weather', label: 'Weather', icon: FiSun },
    { id: 'health', label: 'Health', icon: FiShield },
    { id: 'settings', label: 'Settings', icon: FiSettings }
  ];

  const renderCompactOverview = () => (
    <div className="compact-overview">
      <div className="overview-main">
        <div className="key-stats">
          <div className="stat-item">
            <span className="stat-icon">⚡</span>
            <span className={`stat-value ${battery_charging ? 'charging' : 'not-charging'}`}>
              {battery_charging ? 'Charging' : 'Not Charging'}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">🌡️</span>
            <span className="stat-value">{temperature_f}°F</span>
          </div>
          <div className="stat-item hardware-health">
            <span className="stat-icon">🔧</span>
            <div className="stat-value">
              <HardwareHealthTracker
                block={block}
                compact={true}
                className="detail-hardware-health"
              />
            </div>
          </div>
          {weather_data && weather_data.weather_status === 'fresh' && (
            <div className="stat-item">
              <span className="stat-icon">☀️</span>
              <span className="stat-value">{weather_data.uv_index}</span>
            </div>
          )}
          {weather_data && weather_data.weather_status === 'fresh' && (
            <div className="stat-item">
              <span className="stat-icon">💧</span>
              <span className="stat-value">{weather_data.weather_humidity || humidity}%</span>
            </div>
          )}
          <div className="stat-item">
            <span className="stat-icon">⏱️</span>
            <span className="stat-value">{getLastSeenDisplay()}</span>
          </div>
        </div>
      </div>
    </div>
  );



  const renderToolsTab = () => (
    <div className="tools-tab-content">
      <DeviceManagementTools
        block={block}
        onPrime={(serial, action, data) => {
          console.log('📋 BlockDetailsView.onPrime called', { serial, action, data, onDeviceAction: !!onDeviceAction });
          onDeviceAction?.(serial, action, data);
        }}
        onProximity={(serial, action, data) => onDeviceAction?.(serial, action, data)}
        onReset={(serial, action, data) => onDeviceAction?.(serial, action, data)}
        onPaymentConfirmed={(serial, action, data) => onDeviceAction?.(serial, action, data)}
        onMotorControl={(serial, action, data) => onDeviceAction?.(serial, action, data)}
        onRunDiagnostics={(serial, action, data) => onDeviceAction?.(serial, action, data)}
        onSensorControl={(serial, action, data) => onDeviceAction?.(serial, action, data)}
        onUVControl={(serial, action, data) => onDeviceAction?.(serial, action, data)}
        onSetMode={(serial, action, data) => {
          console.log('📋 BlockDetailsView.onSetMode called', { serial, action, data, onDeviceAction: !!onDeviceAction });
          onDeviceAction?.(serial, action, data);
        }}
        onClearErrors={(serial, action, data) => onDeviceAction?.(serial, action, data)}
        onClearData={(serial, action) => onDeviceAction?.(serial, action)}
        onFactoryReset={(serial, action) => onDeviceAction?.(serial, action)}
        onConfirmDangerousAction={onConfirmDangerousAction}
        userRole={userRole}
        isAdmin={isAdmin}
        isFranchisee={isFranchisee}
        isOperator={isOperator}
      />
    </div>
  );

  const renderAnalyticsTab = () => (
    <div className="analytics-tab-content">
      <div className="analytics-section">
        <h4>Dispense History</h4>
        <div className="dispense-chart-container">
          {loadingDispenseHistory ? (
            <div className="loading-message">
              <p>Loading dispense history...</p>
            </div>
          ) : dispenseHistoryError ? (
            <div className="error-message">
              <p>Error loading dispense history: {dispenseHistoryError}</p>
              <button onClick={fetchDispenseHistory} className="retry-button">
                Retry
              </button>
            </div>
          ) : (
            <DispenseChart dispenseHistory={dispenseHistory} />
          )}
        </div>
      </div>
    </div>
  );

  const renderHealthTab = () => (
    <div className="health-tab-content">
      <HealthTab 
        block={block} 
        onRefresh={() => {
          // OPTIMIZATION: Don't reload entire page - let polling handle updates
          console.log('♻️ Health data updated - polling will refresh data shortly');
        }}
      />
    </div>
  );

  const renderWeatherTab = () => (
    <div className="weather-tab-content">
      <WeatherTab serial={serial} />
    </div>
  );

  const renderSunscreenTab = () => {
    // Use real slots data from block, fallback to empty array if not available
    const realSlots = block?.slots || [];
    
    // Default sunscreen types if not provided
    const defaultSunscreenTypes = [
      { id: 'SPF15', name: 'SPF 15', spf: 15, color: '#FFE5B4', icon: '🧴' },
      { id: 'SPF30', name: 'SPF 30', spf: 30, color: '#FFD700', icon: '🧴' },
      { id: 'SPF50', name: 'SPF 50', spf: 50, color: '#FF6B35', icon: '🧴' },
      { id: 'SPF100', name: 'SPF 100', spf: 100, color: '#FF4500', icon: '🧴' }
    ];

    return (
      <div className="sunscreen-tab-content">
        {realSlots.length > 0 ? (
          <SlotManager
            slots={realSlots}
            deviceSerial={serial}
            sunscreenTypes={defaultSunscreenTypes}
            onRefillSlot={onRefillSlot}
            userRole={userRole}
            isAdmin={isAdmin}
            isFranchisee={isFranchisee}
            isOperator={isOperator}
          />
        ) : (
          <div className="no-slots-message">
            <h4>No Sunscreen Slots Configured</h4>
            <p>This device doesn&apos;t have any sunscreen slots configured yet. Contact your administrator to set up sunscreen dispensing.</p>
          </div>
        )}
      </div>
    );
  };

  const renderSettingsTab = () => (
    <div className="settings-tab-content">
      <div className="settings-sections">
        {/* Location Management */}
        <div className="settings-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('location')}
          >
            <FiMapPin className="section-icon" />
            <h4>Location Management</h4>
            <FiChevronRight className={`chevron ${expandedSections.location ? 'expanded' : ''}`} />
          </div>
          {expandedSections.location && (
            <div className="section-content">
              <div className="current-info">
                <div className="info-item">
                  <span className="info-label">Current Location:</span>
                  <span className="info-value">{location}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Address:</span>
                  <span className="info-value">{address}</span>
                </div>
              </div>

              <form className="location-form" onSubmit={handleLocationUpdate}>
                <div className="form-group">
                  <label>Update Location</label>
                  <input
                    type="text"
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    placeholder="Enter new location..."
                    className="location-input"
                  />
                </div>
                <button type="submit" className="location-submit-btn">
                  Update Location
                </button>
              </form>

              <form className="address-form" onSubmit={handleAddressUpdate}>
                <div className="form-group">
                  <label>Update Address</label>
                  <input
                    type="text"
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    placeholder="Enter new address..."
                    className="address-input"
                  />
                </div>
                <button type="submit" className="address-submit-btn">
                  Update Address
                </button>
              </form>
            </div>
          )}
        </div>


      </div>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'analytics':
        return renderAnalyticsTab();
      case 'sunscreen':
        return renderSunscreenTab();
      case 'health':
        return renderHealthTab();
      case 'weather':
        return renderWeatherTab();
      case 'settings':
        return renderSettingsTab();
      default:
        return renderToolsTab();
    }
  };

  return (
    <div className="block-details-view">
      {renderCompactOverview()}
      
      {isExpanded && (
        <div className="block-tabs">
          <div className="tab-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon className="tab-icon" />
                {tab.label}
              </button>
            ))}
          </div>
          
          <div className="tab-content">
            {renderActiveTab()}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockDetailsView;
